import { AutoComplete, AutoCompleteProps } from 'antd';
import { debounce, get } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import FieldWithLabel from '../../../../common/components/Fields/FieldWithLabel';
import { FieldProps } from '../../../../common/components/Fields/types';
import { useAdminVendorsInOrderSearchLazyQuery } from '../../graphql/venders.generated';

type VenderAutoCompleteFieldProps<TFieldValues extends FieldValues> =
  AutoCompleteProps &
    FieldProps & {
      control: Control<TFieldValues>;
      name: Path<TFieldValues>;
    };

export default function VenderAutoCompleteField<
  TFieldValues extends FieldValues,
>({
  name,
  label,
  required,
  control,
  hideError,
  ...props
}: VenderAutoCompleteFieldProps<TFieldValues>) {
  const [fetchVenders, { data }] = useAdminVendorsInOrderSearchLazyQuery();

  const fetchItems = useCallback(
    async (v: string) => {
      await fetchVenders({
        variables: {
          vendorsInput: {
            search: {
              vendorNameOrCode: v || undefined,
            },
            sortings: [{ field: 'UPDATED_AT', order: 'DESC' }],
            pagination: {
              skip: 0,
              take: 20,
            },
          },
        },
      });
    },
    [fetchVenders]
  );

  const handleSearch = useCallback(
    debounce(async (v: string) => {
      await fetchItems(v);
    }, 200),
    []
  );

  const options = useMemo(() => {
    return (data?.adminVendors?.vendors || []).map((vendor) => {
      return {
        label: vendor.name,
        value: vendor.name,
        key: vendor.id,
      };
    });
  }, [data]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState }) => {
        const error = get(formState.errors, name);
        const errorMessage =
          !hideError && typeof error?.message === 'string'
            ? error.message
            : undefined;
        const status = !hideError && errorMessage ? 'error' : '';

        return (
          <FieldWithLabel label={label} required={required}>
            <AutoComplete
              options={options}
              onSearch={handleSearch}
              className='w-full'
              status={status}
              optionRender={(oriOption) => {
                return (
                  <>
                    {oriOption.label}
                    <span className='ml-1 text-xs text-black/25'>
                      {oriOption.key}
                    </span>
                  </>
                );
              }}
              {...field}
              {...props}
            />

            {!hideError && errorMessage && (
              <p className={'text-xs text-red-500'}>{errorMessage}</p>
            )}
          </FieldWithLabel>
        );
      }}
    />
  );
}
